// const getRenderedCellValue = (value: string | number | undefined | null) =>
//   value === 0 ? value : getFixedNumber(value) || '-';

import { createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';
import { format, isToday } from 'date-fns';
import { TFunction } from 'i18next';
import { formatNumericValue } from 'utils/general';

import { useCreateEditContractUtils } from '@/modules/workforce/Contracts/components/CreateAndEdit/logic';
import { useContractDetails } from '@/modules/workforce/Contracts/components/Details/logic';
import EndDeleteContract from '@/modules/workforce/Contracts/components/Details/Modals/EndDeleteContract';
import { IContracts } from '@/modules/workforce/Contracts/types';
import { CircularBgImage, Typography } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import CloseSquare from '~/images/shared/CloseSquare.svg';
import Edit from '~/images/shared/edit.svg';
import ExleminationMark from '~/images/shared/ExleminationMark.svg';
import DotsMenu from '~/images/shared/HorizontalHollowDots.svg';
import Profile from '~/images/shared/Profile.svg';
import Slash from '~/images/shared/Slash.svg';

const columnHelper = createColumnHelper<IContracts.IContractsListSchema>();

export const useGetContractsColumns = () => {
  const { openModal } = useModal();
  const [isDemoFarm] = useStore((store) => store?.isDemoFarm);

  const { checkCanEditContract, openEditContractModal } =
    useCreateEditContractUtils();
  const { checkUnitMismatch } = useContractDetails();

  const contractsColumns = (t: TFunction, currency: string) => {
    return [
      columnHelper.accessor('supplierAssignmentId', {
        cell: ({
          getValue,
          row: {
            original: { contractStatus, dailyWage, pricingUnits },
          },
        }) => (
          <div className='flex items-center gap-2'>
            <div className='flex items-center gap-1 text-base font-medium text-text-default'>
              <span>{getValue()}</span>
              {/* unit mismatch or auto generated */}
              {contractStatus === 0 ? (
                <TippyTooltip
                  placement='top'
                  maxWidth={300}
                  content={
                    <Typography variant='caption' weight='semiBold'>
                      {t('incomplete-contract')}
                    </Typography>
                  }
                >
                  <span className='text-tertiary-default'>
                    <ExleminationMark />
                  </span>
                </TippyTooltip>
              ) : (
                /* daily wage with units */
                checkUnitMismatch(contractStatus, dailyWage, pricingUnits) && (
                  <TippyTooltip
                    placement='top'
                    maxWidth={300}
                    content={
                      <Typography
                        variant='caption'
                        weight='semiBold'
                        className='py-8 px-12'
                      >
                        {t('contract-with-incomplete-unit')}
                      </Typography>
                    }
                  >
                    <span className='text-error-400'>
                      <ExleminationMark />
                    </span>
                  </TippyTooltip>
                )
              )}
            </div>
          </div>
        ),
        header: () => t('contract-id'),
      }),

      columnHelper.accessor('supplierName', {
        cell: ({
          getValue,
          row: {
            original: { createdBy },
          },
        }) => (
          <div className='flex items-center gap-2'>
            <div className='relative flex items-center justify-center rounded-full bg-outline-default p-9 text-28'>
              <Profile />
            </div>
            <div className='flex flex-col'>
              <div className='text-base font-bold text-text-default'>
                {getValue()}
              </div>
              <div className='text-14 text-surface-10'>
                {t('createdby')}: {createdBy}
              </div>
            </div>
          </div>
        ),
        header: () => t('crew'),
      }),
      columnHelper.accessor('jobName', {
        cell: ({
          getValue,
          row: {
            original: { cropImage },
          },
        }) => (
          <div className='flex items-center gap-2'>
            <CircularBgImage
              imageSrc={cropImage ?? ''}
              bgColor='bg-outline-default'
              size='xl'
            />
            <div className='text-base text-text-default'>{getValue()}</div>
          </div>
        ),
        header: () => t('Job'),
      }),
      columnHelper.accessor('startDate', {
        cell: ({
          getValue,
          row: {
            original: { endDate },
          },
        }) => (
          <div className='text-base text-surface-10'>
            {format(new Date(getValue().replace(/Z$/, '')), 'dd MMM yyyy')} to -{' '}
            {format(new Date(endDate.replace(/Z$/, '')), 'dd MMM yyyy')}
          </div>
        ),
        header: () => t('duration'),
      }),
      columnHelper.accessor('dailyWage', {
        cell: ({ getValue }) => (
          <div>{t(getValue() === 0 ? 'piecework-rate' : 'daily')}</div>
        ),
        header: () => t('contract-type'),
      }),
      columnHelper.accessor('pricingUnits', {
        cell: ({
          getValue,
          row: {
            original: { dailyWage, dailyRate },
          },
        }) => {
          const list = getValue();
          return dailyWage === 1 ? (
            `${dailyRate ? formatNumericValue(dailyRate) : 0} ${currency}`
          ) : list?.length ? (
            <PartialListWithTooltip
              limit={1}
              items={list?.map((item) => ({
                description: `${
                  item.unit
                    ? `${
                        item.price ? formatNumericValue(item.price) : 0
                      } ${currency}/${item.unit}`
                    : `${
                        item.price ? formatNumericValue(item.price) : 0
                      } ${currency}`
                }`,
              }))}
            />
          ) : (
            '-'
          );
        },
        header: () => t('rate-rate'),
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({ row: { original } }) => {
          const canEditContract = checkCanEditContract(original);
          return (
            <div
              className={classNames('flex items-center gap-24 text-24')}
              onClick={(e) => e.stopPropagation()}
            >
              <TippyTooltip
                // tooltip enabled if !canEditContract && isToday
                disabled={
                  canEditContract || !isToday(new Date(original.endDate))
                }
                content={t('contracts-edit-disabled-today')}
              >
                <span
                  className={classNames(
                    !canEditContract && 'pointer-events-none opacity-5'
                  )}
                  onClick={() =>
                    openEditContractModal(original.supplierAssignmentId)
                  }
                >
                  <Edit />
                </span>
              </TippyTooltip>
              {original.status !== 3 && !isToday(new Date(original.endDate)) && (
                <TippyTooltip
                  trigger='click'
                  content={
                    <div
                      className='flex cursor-pointer items-center gap-2 text-error-400'
                      onClick={() =>
                        openModal(
                          isDemoFarm ? (
                            <DemoCTA />
                          ) : (
                            <EndDeleteContract
                              redirectOnSuccess={false}
                              supplierAssignmentId={
                                original.supplierAssignmentId
                              }
                              status={
                                original.status === 1 ? 'Active' : 'Upcoming'
                              }
                            />
                          )
                        )
                      }
                    >
                      {original.status === 1 ? (
                        <Slash width='24' height='24' />
                      ) : (
                        <CloseSquare width='24' height='24' />
                      )}
                      <span>
                        {t(
                          original.status === 1
                            ? 'End contract'
                            : 'Delete contract'
                        )}
                      </span>
                    </div>
                  }
                >
                  <DotsMenu />
                </TippyTooltip>
              )}
            </div>
          );
        },
      }),
    ];
  };

  return { contractsColumns };
};
